/**
 * Board meeting records — list view.
 *
 * Ported from `dashboard/src/views/shareholder/Meetings.jsx` (the
 * 2026-05-28 publisher-dashboard surface). Backend contract unchanged
 * (`GET /board/meetings`), so this only re-uses the same endpoint
 * with the investor-console's styling/atoms.
 *
 * Non-board shareholders only see concluded meetings — that filter is
 * server-side, so the SPA renders whatever comes back without an
 * extra check. Executive-session content is server-side redacted from
 * the meeting payload before it reaches us.
 */
import { Link } from 'react-router-dom';
import { CheckCircle, Video } from 'lucide-react';
import { useBoardMeetings } from '@/hooks/useMeetings';
import { fmtDateTime } from '@/lib/format';
import { Card, SectionLabel, Skeleton } from '@/components/ui/atoms';

export function MeetingsScreen() {
  const { data, isLoading } = useBoardMeetings();
  const meetings = data?.meetings || [];

  return (
    <div className="space-y-6">
      <header>
        <SectionLabel>Board Meeting Records</SectionLabel>
        <h1 className="mt-1 text-2xl font-bold text-nurev-text">
          Published Meetings
        </h1>
        <p className="mt-1 text-sm text-nurev-muted">
          Concluded meetings and voting outcomes. You&apos;ll receive an email
          when the board publishes a new record.
        </p>
      </header>

      <Card>
        {isLoading && (
          <div className="space-y-2 p-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {!isLoading && meetings.length === 0 && (
          <div className="py-10 text-center text-sm text-nurev-muted">
            No concluded meetings yet. Records will appear here as the board
            publishes results.
          </div>
        )}

        {!isLoading && meetings.length > 0 && (
          <ul className="divide-y divide-nurev-border">
            {meetings.map(m => (
              <li key={m._id}>
                <Link
                  to={`/meetings/${m._id}`}
                  className="flex items-center justify-between gap-3 rounded-md px-3 py-3 transition-colors hover:bg-white/[0.03]"
                >
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
                      <CheckCircle className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold text-nurev-text">
                        {m.name}
                      </div>
                      <div className="mt-0.5 text-[11px] text-nurev-muted">
                        <span className="capitalize">{m.meetingType}</span> ·{' '}
                        {fmtDateTime(m.concludedAt || m.scheduledAt)}
                        {m.recordingUrl && (
                          <span className="ml-2 inline-flex items-center gap-1 text-emerald-300">
                            <Video className="h-3 w-3" /> Recording
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
